// Shared types and helpers for the Inspiration tab.

export interface InspirationVideo {
  videoId: string;
  title: string;
  description: string;
  channelTitle: string;
  publishedAt: string | null;
  thumbnail: string | null;
}

export interface InspirationCategory {
  // "pasted" is the synthetic group the Inspiration panel prepends when the
  // user analyzes their own link; the API only ever returns the other four.
  id: "eats" | "sip" | "do" | "stay" | "pasted";
  label: string;
  emoji: string;
  videos: InspirationVideo[];
  error?: string;
}

export interface InspirationFeed {
  destination: string;
  fromCache: boolean;
  categories: InspirationCategory[];
}

export type ExtractedPlaceType =
  | "dining"
  | "nightlife"
  | "accommodation"
  | "activity";

export interface ExtractedPlace {
  name: string;
  type: ExtractedPlaceType;
  snippet: string;
}

// Pull the YouTube video id from a few common URL shapes.
// Accepts: youtu.be/<id>, youtube.com/watch?v=<id>,
// youtube.com/shorts/<id>, youtube.com/embed/<id>.
export function extractYoutubeId(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  // Quick id-only check (some users paste just the id). Whole string only:
  // per word, any 11-letter word ("restaurants") would read as an id.
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;
  // A copied link often arrives inside the sentence around it ("check this
  // out https://youtu.be/...", or YouTube's own "<url> via @YouTube"), so
  // every whitespace-separated word gets a look. A URL never holds a space,
  // so splitting on one cannot break a link apart.
  if (/\s/.test(trimmed)) {
    for (const word of trimmed.split(/\s+/)) {
      const id = idFromUrl(word);
      if (id) return id;
    }
    return null;
  }
  return idFromUrl(trimmed);
}

function idFromUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    // A pasted link often arrives without its scheme ("youtube.com/watch?
    // v=..."), which URL refuses to parse. Assume https so those still read.
    const u = new URL(
      /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    );
    if (u.hostname === "youtu.be") {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
    }
    if (u.hostname.endsWith("youtube.com")) {
      if (u.pathname === "/watch") {
        const v = u.searchParams.get("v");
        return v && /^[A-Za-z0-9_-]{11}$/.test(v) ? v : null;
      }
      const segMatch = u.pathname.match(/^\/(?:shorts|embed|live)\/([A-Za-z0-9_-]{11})/);
      if (segMatch) return segMatch[1];
    }
    return null;
  } catch {
    return null;
  }
}

// Detect non-YouTube social URLs so we can show a friendly "coming soon".
// "none" is not a link at all (a channel name, a search phrase), which is a
// different mistake from a link we cannot read yet and deserves to be told
// apart in the message the user sees.
export function detectPlatform(
  url: string
):
  | "youtube"
  | "youtube-other"
  | "tiktok"
  | "instagram"
  | "xiaohongshu"
  | "unknown"
  | "none" {
  const lower = url.trim().toLowerCase();
  if (extractYoutubeId(lower)) return "youtube";
  // A YouTube link with no video in it (a channel, a playlist, a search) is
  // a different mistake from a platform we cannot read yet: the reader has
  // to hear that we want one video, not that we cannot read YouTube.
  if (lower.includes("youtube.com") || lower.includes("youtu.be"))
    return "youtube-other";
  if (lower.includes("tiktok.com")) return "tiktok";
  if (lower.includes("instagram.com")) return "instagram";
  if (lower.includes("xiaohongshu.com") || lower.includes("xhslink.com"))
    return "xiaohongshu";
  return looksLikeUrl(lower) ? "unknown" : "none";
}

// A scheme, or a bare host with a dot and a plausible TLD. Deliberately
// loose: everything it lets through is still checked for a video id.
function looksLikeUrl(value: string): boolean {
  if (!value) return false;
  if (/^[a-z][a-z0-9+.-]*:\/\//.test(value)) return true;
  return /^[^\s/]+\.[a-z]{2,}(?:[/?#]|$)/.test(value);
}
